import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Users,
  Search,
  Plus,
  Building2,
  Filter,
  Download,
  ArrowUpDown,
  Eye,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { trpc } from "@/lib/trpc";
import type { Lead, LeadStatus, LeadSource } from "./SalesPipeline";

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  LeadStatus,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  PROSPECT: { label: "Prospect", variant: "outline" },
  DEMO: { label: "Démo", variant: "secondary" },
  NEGOCIATION: { label: "Négociation", variant: "default" },
  GAGNE: { label: "Gagné", variant: "default" },
  PERDU: { label: "Perdu", variant: "destructive" },
};

const SOURCE_LABELS: Record<LeadSource, string> = {
  INBOUND: "Entrant",
  OUTBOUND: "Sortant",
  TRAVAUX_BTP: "TravauxBTP",
  REFERRAL: "Référence",
  OTHER: "Autre",
};

function ScoreBar({ score }: { score: number }) {
  const color = score >= 80 ? "bg-success" : score >= 60 ? "bg-warning" : "bg-destructive";
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs text-muted-foreground">{score}</span>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SalesLeads() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAdd = canWrite(user?.staffRole);

  const { data: leadsData, isLoading, refetch } = trpc.sales.leads.list.useQuery();
  const createMutation = trpc.sales.leads.create.useMutation({
    onSuccess: () => {
      toast.success("Lead créé");
      setShowCreateDialog(false);
      setForm({ name: "", company: "", email: "", phone: "", source: "INBOUND", estimatedValue: "", notes: "" });
      refetch();
    },
    onError: () => toast.error("Erreur lors de la création"),
  });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<LeadStatus | "ALL">("ALL");
  const [sourceFilter, setSourceFilter] = useState<LeadSource | "ALL">("ALL");
  const [sortBy, setSortBy] = useState<"score" | "createdAt" | "estimatedValue">("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    source: "INBOUND" as LeadSource,
    estimatedValue: "",
    notes: "",
  });

  const leads = (leadsData ?? []) as Lead[];

  // Filter + sort (client-side)
  const filtered = leads
    .filter((l) => {
      const matchesSearch =
        !search ||
        l.name.toLowerCase().includes(search.toLowerCase()) ||
        l.company.toLowerCase().includes(search.toLowerCase()) ||
        l.email.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === "ALL" || l.status === statusFilter;
      const matchesSource = sourceFilter === "ALL" || l.source === sourceFilter;
      return matchesSearch && matchesStatus && matchesSource;
    })
    .sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      if (sortBy === "score") return (a.score - b.score) * dir;
      if (sortBy === "estimatedValue") return (a.estimatedValue - b.estimatedValue) * dir;
      return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir;
    });

  function toggleSort(field: typeof sortBy) {
    if (sortBy === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(field); setSortDir("desc"); }
  }

  function handleCreateLead() {
    if (!form.name || !form.company || !form.email) {
      toast.error("Nom, entreprise et email sont requis", { role: "alert" });
      return;
    }
    createMutation.mutate({
      name: form.name,
      company: form.company,
      email: form.email,
      phone: form.phone,
      source: form.source,
      estimatedValue: Number(form.estimatedValue) || 0,
      notes: form.notes,
      status: "PROSPECT",
    });
  }

  function handleExportCSV() {
    const header = "Nom,Entreprise,Email,Téléphone,Statut,Score,Source,Valeur estimée\n";
    const rows = filtered
      .map(
        (l) =>
          `"${l.name}","${l.company}","${l.email}","${l.phone}","${STATUS_CONFIG[l.status].label}",${l.score},"${SOURCE_LABELS[l.source]}",${l.estimatedValue}`
      )
      .join("\n");
    const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export CSV téléchargé");
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users aria-hidden="true" className="h-6 w-6 text-primary" />
            Leads Sales
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {filtered.length} lead{filtered.length > 1 ? "s" : ""} —{" "}
            {filtered.reduce((s, l) => s + l.estimatedValue, 0).toLocaleString("fr-FR")} €/an de pipeline
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCSV} aria-label="Exporter les leads en CSV">
            <Download aria-hidden="true" className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          {canAdd && (
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus aria-hidden="true" className="h-4 w-4 mr-2" />
              Nouveau lead
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative">
          <Search aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            aria-label="Rechercher un lead"
            placeholder="Rechercher un lead..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 w-64"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as LeadStatus | "ALL")}>
          <SelectTrigger className="w-[150px]" aria-label="Filtrer par statut">
            <Filter aria-hidden="true" className="h-3.5 w-3.5 mr-2" />
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tous statuts</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={(v) => setSourceFilter(v as LeadSource | "ALL")}>
          <SelectTrigger className="w-[150px]" aria-label="Filtrer par source">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Toutes sources</SelectItem>
            {Object.entries(SOURCE_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
              Chargement…
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => toggleSort("score")}
                  >
                    <div className="flex items-center gap-1">
                      Score
                      <ArrowUpDown aria-hidden="true" className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => toggleSort("estimatedValue")}
                  >
                    <div className="flex items-center gap-1">
                      Valeur
                      <ArrowUpDown aria-hidden="true" className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead>Assigné</TableHead>
                  <TableHead
                    className="cursor-pointer select-none"
                    onClick={() => toggleSort("createdAt")}
                  >
                    <div className="flex items-center gap-1">
                      Créé le
                      <ArrowUpDown aria-hidden="true" className="h-3 w-3" />
                    </div>
                  </TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-12">
                      Aucun lead trouvé
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((lead) => {
                  const statusCfg = STATUS_CONFIG[lead.status];
                  return (
                    <TableRow
                      key={lead.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/sales/leads/${lead.id}`)}
                    >
                      <TableCell>
                        <div className="font-medium text-sm">{lead.name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Building2 aria-hidden="true" className="h-3 w-3" />
                          {lead.company}
                        </div>
                        <div className="text-xs text-muted-foreground">{lead.email}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusCfg.variant} className="text-xs">
                          {statusCfg.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {SOURCE_LABELS[lead.source]}
                      </TableCell>
                      <TableCell>
                        <ScoreBar score={lead.score} />
                      </TableCell>
                      <TableCell className="text-sm font-medium">
                        {lead.estimatedValue.toLocaleString("fr-FR")} €
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{lead.assignee || "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(lead.createdAt).toLocaleDateString("fr-FR")}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Voir le lead ${lead.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/sales/leads/${lead.id}`);
                          }}
                        >
                          <Eye aria-hidden="true" className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nouveau lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="lead-name" className="text-xs">Nom *</Label>
                <Input
                  id="lead-name"
                  placeholder="Jean Dupont"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="lead-company" className="text-xs">Entreprise *</Label>
                <Input
                  id="lead-company"
                  placeholder="BTP Pro"
                  value={form.company}
                  onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="lead-email" className="text-xs">Email *</Label>
              <Input
                id="lead-email"
                type="email"
                placeholder="contact@btp-pro.fr"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Téléphone</Label>
                <Input
                  placeholder="06 12 34 56 78"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Valeur (€/an)</Label>
                <Input
                  type="number"
                  placeholder="2400"
                  value={form.estimatedValue}
                  onChange={(e) => setForm((f) => ({ ...f, estimatedValue: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <Select
                value={form.source}
                onValueChange={(v) => setForm((f) => ({ ...f, source: v as LeadSource }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SOURCE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                placeholder="Contexte, besoins..."
                rows={3}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateLead} disabled={createMutation.isPending}>
              {createMutation.isPending ? "Création…" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
